import { logger } from '@/utils/logger'

/**
 * ETagを管理するクラス
 * ユーザー情報の楽観的ロック（Optimistic Locking）を実装
 */
export class ETagManager {
  private etagCache = new Map<string, string>()

  /**
   * ETagをキャッシュに保存
   */
  setETag(resourceKey: string, etag: string): void {
    this.etagCache.set(resourceKey, etag)
    logger.debug('ETag cached', { resourceKey, etag })
  }

  /**
   * キャッシュからETagを取得
   */
  getETag(resourceKey: string): string | undefined {
    return this.etagCache.get(resourceKey)
  }

  /**
   * ETagをキャッシュから削除
   */
  clearETag(resourceKey: string): void {
    this.etagCache.delete(resourceKey)
    logger.debug('ETag cleared', { resourceKey })
  }

  /**
   * 全てのETagをクリア
   */
  clearAllETags(): void {
    this.etagCache.clear()
    logger.debug('All ETags cleared')
  }

  /**
   * ユーザー情報のリソースキーを生成
   */
  getUserResourceKey(email: string): string {
    return `user:${email}`
  }

  /**
   * レスポンスからETagヘッダーを抽出し、キャッシュに保存
   */
  extractAndCacheETag(response: Response, resourceKey: string): string | null {
    const etag = response.headers.get('ETag')
    if (etag) {
      this.setETag(resourceKey, etag)
      return etag
    }
    return null
  }

  /**
   * If-Matchヘッダーを生成
   */
  generateIfMatchHeaders(resourceKey: string): Record<string, string> {
    const etag = this.getETag(resourceKey)
    if (!etag) {
      throw new Error(`ETag not found for resource: ${resourceKey}`)
    }

    return {
      'If-Match': etag,
    }
  }

  /**
   * ETagコンフリクトエラーかどうかを判定
   */
  isETagConflictError(error: unknown): boolean {
    return (
      error instanceof Error && 'status' in error && (error as { status?: number }).status === 412
    )
  }

  /**
   * ETagコンフリクト発生時のエラーメッセージを生成
   */
  getConflictErrorMessage(resourceKey: string): string {
    return `Resource '${resourceKey}' was modified by another request. Please refresh and try again.`
  }
}

// シングルトンインスタンス
export const etagManager = new ETagManager()
